import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Space, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateDistrictMutation,
  useDeleteDistrictMutation,
  useGetDistrictsQuery,
  useGetStatesQuery,
  useImportDistrictsMutation,
  useUpdateDistrictMutation,
} from '@/modules/locations/locationsApi';
import type { CreateDistrictPayload, DistrictMaster } from '@/types/location';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmPermanentLocationDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { useLocationBulkUpload } from './components/useLocationBulkUpload';
import styles from './Locations.module.scss';

type DistrictFormValues = CreateDistrictPayload;

export default function Districts() {
  const notify = useNotify();
  const [form] = Form.useForm<DistrictFormValues>();
  const { data, isLoading, isFetching, refetch } = useGetDistrictsQuery();
  const { data: statesData, isLoading: isStatesLoading } = useGetStatesQuery({ status: 'active' });
  const [createDistrict, { isLoading: isCreating }] = useCreateDistrictMutation();
  const [updateDistrict, { isLoading: isUpdating }] = useUpdateDistrictMutation();
  const [deleteDistrict, { isLoading: isDeleting }] = useDeleteDistrictMutation();
  const [importDistricts, { isLoading: isImporting }] = useImportDistrictsMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<DistrictMaster | null>(null);
  const { bulkUploadControls } = useLocationBulkUpload({
    entityLabel: 'District',
    entityLabelPlural: 'Districts',
    exportPath: '/districts/export',
    templatePath: '/districts/export-template',
    exportFilename: 'districts.xlsx',
    templateFilename: 'districts-template.xlsx',
    importMutation: importDistricts,
    isImporting,
    onImported: () => {
      void refetch();
    },
  });

  useGlobalLoader(isFetching && !isLoading);

  const districts = data?.results ?? [];
  const stateOptions = (statesData?.results ?? []).map((state) => ({
    label: state.name,
    value: state.id,
  }));

  useEffect(() => {
    if (!modalOpen) return;
    form.setFieldsValue({
      stateId: editingDistrict?.stateId,
      name: editingDistrict?.name ?? '',
      isActive: editingDistrict?.isActive ?? true,
    });
  }, [editingDistrict, form, modalOpen]);

  const openCreate = () => {
    setEditingDistrict(null);
    setModalOpen(true);
  };

  const openEdit = (district: DistrictMaster) => {
    setEditingDistrict(district);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingDistrict(null);
    form.resetFields();
  };

  const handleSubmit = async (values: DistrictFormValues) => {
    const body = { ...values, name: values.name.trim() };
    const result = await resolveMutationResult(
      editingDistrict
        ? updateDistrict({ id: editingDistrict.id, body })
        : createDistrict(body),
    );

    if (result.ok) {
      notify.success(result.message ?? (editingDistrict ? 'District updated.' : 'District created.'));
      closeModal();
      return;
    }

    notify.error(result.message);
  };

  const handleDelete = async (district: DistrictMaster) => {
    const confirmed = await confirmPermanentLocationDelete(
      district.name,
      'All constituencies and assemblies under this district',
    );
    if (!confirmed) return;

    const result = await resolveMutationResult(deleteDistrict(district.id));
    if (result.ok) {
      notify.success(result.message ?? 'District deleted.');
      return;
    }

    notify.error(result.message);
  };

  const columns: ColumnsType<DistrictMaster> = [
    {
      title: 'DISTRICT',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <div className={styles.nameCell}>{name}</div>
          <div className={styles.subText}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'STATE',
      dataIndex: 'stateName',
      key: 'stateName',
      render: (stateName: string) => stateName || '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      render: (isActive: boolean) => (
        <span className={isActive ? styles.statusActive : styles.statusInactive}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'UPDATED',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_value, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {bulkUploadControls}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add district
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<DistrictMaster>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={districts}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
          }}
        />
      </div>

      <Modal
        title={editingDistrict ? 'Edit District' : 'Add District'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        confirmLoading={isCreating || isUpdating}
        destroyOnClose
      >
        <Form<DistrictFormValues>
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="State"
            name="stateId"
            rules={[{ required: true, message: 'Please select state.' }]}
          >
            <Select
              showSearch
              loading={isStatesLoading}
              options={stateOptions}
              optionFilterProp="label"
              placeholder="Select state"
            />
          </Form.Item>
          <Form.Item
            label="District"
            name="name"
            rules={[{ required: true, message: 'Please enter district name.' }]}
          >
            <Input placeholder="Enter district name" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
